import { useCivicFacilities, useServices } from '@services/common'
import { useCreateCivicFacilities } from '@services/common/useCreateCivicFacilities'
import { useCreateService } from '@services/common/useCreateService'
import { usePatchCivicFacilities } from '@services/common/usePatchCivicFacilities'
import { usePatchService } from '@services/common/usePatchService'
import { useUpdateFlat } from '@services/flats/mutations/useUpdateFlat'
import { FlatWrite } from '@services/flats/types'
import { Wrench } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { FlatDetail } from 'types/api'

import { Card } from '@/components/ui/card'
import { CatalogMultiSelect } from '@/components/ui/catalog-multi-select'
import { CatalogRenameDialog } from '@/components/ui/catalog-rename-dialog'
import type { ComboboxOption } from '@/components/ui/combobox'
import { Label } from '@/components/ui/label'

type CatalogItem = {
  id?: string
  name?: string | null
  name_sk?: string | null
  name_en?: string | null
}
type RichOption = ComboboxOption & { nameSk?: string; nameEn?: string }

function useDebounced(value: string, delay = 300) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

function useAccumulatedOptions(
  data: CatalogItem[] | undefined,
  initial: { id: string; name: string }[]
) {
  const [options, setOptions] = useState<RichOption[]>(() =>
    initial.map((i) => ({ value: i.id, label: i.name }))
  )
  useEffect(() => {
    if (!data) return
    // Upsert by id so labels re-localize and name_sk/name_en stay in sync.
    setOptions((prev) => {
      const map = new Map(prev.map((o) => [o.value, o]))
      data.forEach((d) => {
        if (!d.id) return
        map.set(d.id, {
          value: d.id,
          label: d.name ?? d.name_sk ?? '',
          nameSk: d.name_sk ?? undefined,
          nameEn: d.name_en ?? undefined
        })
      })
      return Array.from(map.values())
    })
  }, [data])
  return [options, setOptions] as const
}

type RenameTarget = {
  kind: 'service' | 'civic'
  id: string
  nameSk: string
  nameEn: string
} | null

export function FlatServiceForm({ flat }: { flat: FlatDetail }) {
  const { t } = useTranslation('flat')
  const { mutate: updateFlat } = useUpdateFlat()

  const [services, setServices] = useState<string[]>(
    (flat.services ?? []).filter((s) => s.id).map((s) => s.id!)
  )
  const [civic, setCivic] = useState<string[]>(
    (flat.civic_facilities ?? []).filter((c) => c.id).map((c) => c.id!)
  )

  // --- Service catalog ---
  const [serviceSearch, setServiceSearch] = useState('')
  const serviceData = useServices({ search: useDebounced(serviceSearch) })
  const [serviceOptions, setServiceOptions] = useAccumulatedOptions(
    serviceData.data,
    (flat.services ?? [])
      .filter((s) => s.id)
      .map((s) => ({ id: s.id!, name: s.name ?? '' }))
  )
  const { mutate: createService } = useCreateService()
  const { mutate: patchService, isPending: patchingService } = usePatchService()

  // --- Civic facility catalog ---
  const [civicSearch, setCivicSearch] = useState('')
  const civicData = useCivicFacilities({ search: useDebounced(civicSearch) })
  const [civicOptions, setCivicOptions] = useAccumulatedOptions(
    civicData.data,
    (flat.civic_facilities ?? [])
      .filter((c) => c.id)
      .map((c) => ({ id: c.id!, name: c.name ?? '' }))
  )
  const { mutate: createCivic } = useCreateCivicFacilities()
  const { mutate: patchCivic, isPending: patchingCivic } =
    usePatchCivicFacilities()

  const [rename, setRename] = useState<RenameTarget>(null)

  // Auto-save the flat's services + civic facilities (debounced), skip mount.
  const skipSave = useRef(true)
  useEffect(() => {
    if (skipSave.current) {
      skipSave.current = false
      return
    }
    const timer = setTimeout(() => {
      updateFlat({
        id: flat.id,
        body: { services, civic_facilities: civic } as unknown as FlatWrite
      })
    }, 2000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, civic])

  const handleCreateService = (name: string) =>
    createService(
      { body: { name } },
      {
        onSuccess: (created: CatalogItem) => {
          if (!created.id) return
          setServiceOptions((prev) => [
            ...prev,
            { value: created.id!, label: created.name ?? name }
          ])
          setServices((prev) => [...prev, created.id!])
        }
      }
    )

  const handleCreateCivic = (name: string) =>
    createCivic(
      { body: { name } },
      {
        onSuccess: (created: CatalogItem) => {
          if (!created.id) return
          setCivicOptions((prev) => [
            ...prev,
            { value: created.id!, label: created.name ?? name }
          ])
          setCivic((prev) => [...prev, created.id!])
        }
      }
    )

  const saveRename = (nameSk: string, nameEn: string) => {
    if (!rename) return
    const body = { name: nameSk, name_sk: nameSk, name_en: nameEn }
    const apply = () => {
      const setter =
        rename.kind === 'service' ? setServiceOptions : setCivicOptions
      setter((prev) =>
        prev.map((o) =>
          o.value === rename.id
            ? { ...o, label: nameSk, nameSk, nameEn }
            : o
        )
      )
      setRename(null)
    }
    if (rename.kind === 'service') {
      patchService({ id: rename.id, body }, { onSuccess: apply })
    } else {
      patchCivic({ id: rename.id, body }, { onSuccess: apply })
    }
  }

  return (
    <Card className="space-y-5 p-5 sm:p-6">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Wrench className="h-5 w-5" />
        </span>
        <h2 className="m-0 font-display text-xl font-bold tracking-tight text-ink">
          {t('checklist.services')}
        </h2>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <Label className="mb-1.5 block">{t('checklist.services')}</Label>
          <CatalogMultiSelect
            value={services}
            onChange={setServices}
            onSearch={setServiceSearch}
            options={serviceOptions}
            loading={serviceData.isLoading}
            placeholder={t('checklist.services')}
            onCreate={handleCreateService}
            onEdit={(o) =>
              setRename({
                kind: 'service',
                id: o.value,
                nameSk: (o as RichOption).nameSk ?? o.label,
                nameEn: (o as RichOption).nameEn ?? ''
              })
            }
          />
        </div>

        <div>
          <Label className="mb-1.5 block">
            {t('checklist.civic_facilities')}
          </Label>
          <CatalogMultiSelect
            value={civic}
            onChange={setCivic}
            onSearch={setCivicSearch}
            options={civicOptions}
            loading={civicData.isLoading}
            placeholder={t('checklist.civic_facilities')}
            onCreate={handleCreateCivic}
            onEdit={(o) =>
              setRename({
                kind: 'civic',
                id: o.value,
                nameSk: (o as RichOption).nameSk ?? o.label,
                nameEn: (o as RichOption).nameEn ?? ''
              })
            }
          />
        </div>
      </div>

      <CatalogRenameDialog
        open={rename !== null}
        title={t('checklist.rename_title')}
        labelSk={t('checklist.name_sk')}
        labelEn={t('checklist.name_en')}
        initialSk={rename?.nameSk ?? ''}
        initialEn={rename?.nameEn ?? ''}
        cancelLabel={t('checklist.no')}
        saveLabel={t('checklist.save')}
        pending={patchingService || patchingCivic}
        onClose={() => setRename(null)}
        onSave={saveRename}
      />
    </Card>
  )
}
